/**
 * Work Ability Card (WAC) Component
 * Skills-based profile that replaces traditional CV
 * Designed for AI job matching
 */
import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, Briefcase, MapPin, Languages, Award, Clock, 
  Plus, X, Save, Loader2, CheckCircle2, AlertCircle,
  ChevronRight, Star, Globe, Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Skill levels - using i18n keys
const SKILL_LEVELS = [
  { value: 'beginner', labelKey: 'workAbilityCard.levels.beginner', descKey: 'workAbilityCard.levels.beginnerDesc' },
  { value: 'intermediate', labelKey: 'workAbilityCard.levels.intermediate', descKey: 'workAbilityCard.levels.intermediateDesc' },
  { value: 'advanced', labelKey: 'workAbilityCard.levels.advanced', descKey: 'workAbilityCard.levels.advancedDesc' },
  { value: 'expert', labelKey: 'workAbilityCard.levels.expert', descKey: 'workAbilityCard.levels.expertDesc' }
];

// Availability types - using i18n keys
const AVAILABILITY_TYPES = [
  { value: 'immediate', labelKey: 'workAbilityCard.availability.immediate' },
  { value: 'two_weeks', labelKey: 'workAbilityCard.availability.twoWeeks' },
  { value: 'one_month', labelKey: 'workAbilityCard.availability.oneMonth' },
  { value: 'negotiable', labelKey: 'workAbilityCard.availability.negotiable' }
];

// Work types - using i18n keys
const WORK_TYPES = [
  { value: 'full_time', labelKey: 'workAbilityCard.workTypes.fullTime' },
  { value: 'part_time', labelKey: 'workAbilityCard.workTypes.partTime' },
  { value: 'contract', labelKey: 'workAbilityCard.workTypes.contract' },
  { value: 'freelance', labelKey: 'workAbilityCard.workTypes.freelance' },
  { value: 'seasonal', labelKey: 'workAbilityCard.workTypes.seasonal' }
];

// Work locations - using i18n keys
const WORK_LOCATIONS = [
  { value: 'on_site', labelKey: 'workAbilityCard.locations.onSite' },
  { value: 'remote', labelKey: 'workAbilityCard.locations.remote' },
  { value: 'hybrid', labelKey: 'workAbilityCard.locations.hybrid' },
  { value: 'willing_to_relocate', labelKey: 'workAbilityCard.locations.willingToRelocate' }
];

// Countries - using i18n country keys
const COUNTRIES = [
  { value: 'Norway', labelKey: 'countries.NO' },
  { value: 'Finland', labelKey: 'countries.FI' },
  { value: 'Sweden', labelKey: 'countries.SE' },
  { value: 'Estonia', labelKey: 'countries.EE' },
  { value: 'Germany', labelKey: 'countries.DE' },
  { value: 'Netherlands', labelKey: 'countries.NL' },
  { value: 'Denmark', labelKey: 'countries.DK' },
  { value: 'Poland', labelKey: 'countries.PL' },
  { value: 'United Kingdom', labelKey: 'countries.GB' },
  { value: 'Ireland', labelKey: 'countries.IE' }
];

// Languages with native names - no translation needed as they show native names
const LANGUAGES = [
  { value: 'English', label: 'English' },
  { value: 'Finnish', label: 'Suomi' },
  { value: 'Swedish', label: 'Svenska' },
  { value: 'Norwegian', label: 'Norsk' },
  { value: 'Estonian', label: 'Eesti' },
  { value: 'German', label: 'Deutsch' },
  { value: 'Polish', label: 'Polski' },
  { value: 'Russian', label: 'Русский' },
  { value: 'Ukrainian', label: 'Українська' }
];

// Language proficiency - using i18n keys
const PROFICIENCY_LEVELS = [
  { value: 'basic', labelKey: 'workAbilityCard.proficiency.basic' },
  { value: 'conversational', labelKey: 'workAbilityCard.proficiency.conversational' },
  { value: 'fluent', labelKey: 'workAbilityCard.proficiency.fluent' },
  { value: 'native', labelKey: 'workAbilityCard.proficiency.native' }
];

export function WorkAbilityCard() {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wac, setWac] = useState(null);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('skills');
  
  // Form state
  const [skills, setSkills] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [totalYears, setTotalYears] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [availability, setAvailability] = useState('negotiable');
  const [workTypes, setWorkTypes] = useState(['full_time']);
  const [workLocations, setWorkLocations] = useState(['on_site']);
  const [preferredCountries, setPreferredCountries] = useState([]);
  const [willingToRelocate, setWillingToRelocate] = useState(false);
  const [headline, setHeadline] = useState('');
  const [summary, setSummary] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  
  // New skill form
  const [newSkill, setNewSkill] = useState({ name: '', level: 'intermediate', category: '' });
  const [newLanguage, setNewLanguage] = useState({ language: '', proficiency: 'conversational', is_native: false });
  const [newCert, setNewCert] = useState({ name: '', issuer: '' });

  useEffect(() => {
    if (token) {
      fetchConfig();
      fetchWAC();
    }
  }, [token]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API}/api/wac/config`);
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Failed to fetch WAC config:', error);
    }
  };

  const fetchWAC = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/wac/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setWac(data);
          populateForm(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch WAC:', error);
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (data) => {
    setSkills(data.skills || []);
    setLanguages(data.languages || []);
    setTotalYears(data.total_years_experience || 0);
    setIndustries(data.primary_industries || []);
    setCertifications(data.certifications || []);
    setAvailability(data.availability);
    setWorkTypes(data.preferred_work_types || ['full_time']);
    setWorkLocations(data.preferred_work_locations || ['on_site']);
    setPreferredCountries(data.preferred_countries || []);
    setWillingToRelocate(data.willing_to_relocate || false);
    setHeadline(data.headline);
    setSummary(data.summary);
    setIsPublic(data.is_public !== false);
  };

  const handleSave = async () => {
    if (skills.length === 0) {
      toast.error(t('workAbilityCard.errors.addSkill'));
      return;
    }
    if (preferredCountries.length === 0) {
      toast.error(t('workAbilityCard.errors.selectCountry'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        skills,
        languages,
        total_years_experience: totalYears,
        primary_industries: industries,
        certifications,
        availability,
        preferred_work_types: workTypes,
        preferred_work_locations: workLocations,
        preferred_countries: preferredCountries,
        willing_to_relocate: willingToRelocate,
        headline: headline || null,
        summary: summary || null,
        is_public: isPublic
      };

      const method = wac ? 'PUT' : 'POST';
      const response = await fetch(`${API}/api/wac/`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setWac(data);
        toast.success(wac ? t('workAbilityCard.updated') : t('workAbilityCard.created'));
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error(t('workAbilityCard.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const addSkill = () => {
    if (!newSkill.name.trim()) {
      toast.error(t('workAbilityCard.errors.enterSkillName'));
      return;
    }
    if (skills.some(s => s.name.toLowerCase() === newSkill.name.toLowerCase())) {
      toast.error(t('workAbilityCard.errors.skillExists'));
      return;
    }
    setSkills([...skills, { ...newSkill, name: newSkill.name.trim() }]);
    setNewSkill({ name: '', level: 'intermediate', category: '' });
  };

  const removeSkill = (index) => {
    setSkills(skills.filter((_, i) => i !== index));
  };

  const addLanguage = () => {
    if (!newLanguage.language) {
      toast.error('Please select a language');
      return;
    }
    if (languages.some(l => l.language === newLanguage.language)) {
      toast.error('Language already added');
      return;
    }
    setLanguages([...languages, { ...newLanguage }]);
    setNewLanguage({ language: '', proficiency: 'conversational', is_native: false });
  };

  const removeLanguage = (index) => {
    setLanguages(languages.filter((_, i) => i !== index));
  };

  const addCertification = () => {
    if (!newCert.name.trim()) {
      toast.error('Please enter certification name');
      return;
    }
    setCertifications([...certifications, { ...newCert, name: newCert.name.trim() }]);
    setNewCert({ name: '', issuer: '' });
  };

  const removeCertification = (index) => {
    setCertifications(certifications.filter((_, i) => i !== index));
  };

  const toggleCountry = (country) => {
    setPreferredCountries(prev =>
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const toggleWorkType = (type) => {
    setWorkTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const toggleWorkLocation = (location) => {
    setWorkLocations(prev =>
      prev.includes(location)
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  };

  // Calculate completion score
  const completionScore = wac?.completion_score || calculateScore();
  
  function calculateScore() {
    let score = 0;
    if (skills.length >= 5) score += 30;
    else if (skills.length >= 3) score += 20;
    else if (skills.length >= 1) score += 10;
    if (languages.length >= 2) score += 15;
    else if (languages.length >= 1) score += 10;
    if (totalYears > 0) score += 10;
    if (industries.length > 0) score += 5;
    if (workTypes.length > 0) score += 5;
    if (preferredCountries.length > 0) score += 5;
    if (headline) score += 10;
    if (summary) score += 5;
    if (certifications.length > 0) score += 10;
    return Math.min(score, 100);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t('workAbilityCard.title')}
              </CardTitle>
              <CardDescription>
                {t('capabilityCard.profileDescription')}
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-gray-500">{t('capabilityCard.profileCompleteness')}</span>
                <span className="font-semibold">{completionScore}%</span>
              </div>
              <Progress value={completionScore} className="w-32" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {wac?.matching_ready ? (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {t('capabilityCard.readyForMatching')}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                <AlertCircle className="h-3 w-3 mr-1" />
                {t('capabilityCard.addMoreInfo')}
              </Badge>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={isPublic} onCheckedChange={setIsPublic} />
              <span className="text-sm text-gray-600">
                {isPublic ? t('capabilityCard.publicProfile') : t('capabilityCard.privateProfile')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Form */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 mb-6">
              <TabsTrigger value="skills" className="flex items-center gap-1">
                <Star className="h-4 w-4" />
                <span className="hidden sm:inline">{t('capabilityCard.tabs.skills')}</span>
              </TabsTrigger>
              <TabsTrigger value="languages" className="flex items-center gap-1">
                <Languages className="h-4 w-4" />
                <span className="hidden sm:inline">{t('capabilityCard.tabs.languages')}</span>
              </TabsTrigger>
              <TabsTrigger value="availability" className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span className="hidden sm:inline">{t('capabilityCard.tabs.availability')}</span>
              </TabsTrigger>
              <TabsTrigger value="profile" className="flex items-center gap-1">
                <Briefcase className="h-4 w-4" />
                <span className="hidden sm:inline">{t('capabilityCard.tabs.profile')}</span>
              </TabsTrigger>
            </TabsList>

            {/* Skills Tab */}
            <TabsContent value="skills" className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <Label className="font-medium">{t('capabilityCard.addSkill')}</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    placeholder={t('capabilityCard.skillNamePlaceholder')}
                    value={newSkill.name}
                    onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
                  />
                  <Select
                    value={newSkill.level}
                    onValueChange={(v) => setNewSkill({ ...newSkill, level: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('capabilityCard.levelPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {SKILL_LEVELS.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {t(level.labelKey)} ({t(level.descKey)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={addSkill}>
                    <Plus className="h-4 w-4 mr-1" />
                    {t('capabilityCard.add')}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">{t('capabilityCard.yourSkills', { count: skills.length })}</Label>
                {skills.length === 0 ? (
                  <p className="text-sm text-gray-500 p-4 text-center border rounded-lg">
                    {t('capabilityCard.noSkillsYet')}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="px-3 py-1.5 flex items-center gap-2"
                      >
                        <span>{skill.name}</span>
                        <span className="text-xs text-gray-500">({skill.level})</span>
                        <button
                          onClick={() => removeSkill(index)}
                          className="hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Languages Tab */}
            <TabsContent value="languages" className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <Label className="font-medium">{t('workAbilityCard.addLanguage')}</Label>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Select
                    value={newLanguage.language}
                    onValueChange={(v) => setNewLanguage({ ...newLanguage, language: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('workAbilityCard.selectLanguage')} />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((lang) => (
                        <SelectItem key={lang.value} value={lang.value}>
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={newLanguage.proficiency}
                    onValueChange={(v) => setNewLanguage({ ...newLanguage, proficiency: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('workAbilityCard.proficiency')} />
                    </SelectTrigger>
                    <SelectContent>
                      {PROFICIENCY_LEVELS.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {t(level.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="native"
                      checked={newLanguage.is_native}
                      onCheckedChange={(c) => setNewLanguage({ ...newLanguage, is_native: c })}
                    />
                    <Label htmlFor="native" className="text-sm">{t('workAbilityCard.native')}</Label>
                  </div>
                  <Button onClick={addLanguage}>
                    <Plus className="h-4 w-4 mr-1" />
                    {t('common.add')}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">{t('workAbilityCard.yourLanguages', { count: languages.length })}</Label>
                {languages.length === 0 ? (
                  <p className="text-sm text-gray-500 p-4 text-center border rounded-lg">
                    {t('workAbilityCard.noLanguagesYet')}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {languages.map((lang, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="px-3 py-1.5 flex items-center gap-2"
                      >
                        <Languages className="h-3 w-3" />
                        <span>{lang.language}</span>
                        <span className="text-xs text-gray-500">({lang.proficiency})</span>
                        {lang.is_native && <span className="text-xs text-blue-600">({t('workAbilityCard.native')})</span>}
                        <button
                          onClick={() => removeLanguage(index)}
                          className="hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Availability Tab */}
            <TabsContent value="availability" className="space-y-6">
              {/* Countries */}
              <div className="space-y-3">
                <Label className="font-medium flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  {t('workAbilityCard.preferredCountries')} <span className="text-red-500">*</span>
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {COUNTRIES.map((country) => (
                    <div
                      key={country.value}
                      onClick={() => toggleCountry(country.value)}
                      className={`
                        flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all
                        ${preferredCountries.includes(country.value)
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'}
                      `}
                    >
                      <Checkbox checked={preferredCountries.includes(country.value)} />
                      <span className="text-sm">{t(country.labelKey)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    id="relocate"
                    checked={willingToRelocate}
                    onCheckedChange={setWillingToRelocate}
                  />
                  <Label htmlFor="relocate" className="text-sm">
                    {t('workAbilityCard.willingToRelocate')}
                  </Label>
                </div>
              </div>

              {/* Availability */}
              <div className="space-y-3">
                <Label className="font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {t('workAbilityCard.availabilityLabel')}
                </Label>
                <Select value={availability} onValueChange={setAvailability}>
                  <SelectTrigger className="w-full md:w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABILITY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {t(type.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Work Types */}
              <div className="space-y-3">
                <Label className="font-medium">{t('workAbilityCard.preferredWorkTypes')}</Label>
                <div className="flex flex-wrap gap-2">
                  {WORK_TYPES.map((type) => (
                    <Badge
                      key={type.value}
                      variant={workTypes.includes(type.value) ? 'default' : 'outline'}
                      className="cursor-pointer px-3 py-1"
                      onClick={() => toggleWorkType(type.value)}
                    >
                      {workTypes.includes(type.value) && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {t(type.labelKey)}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Work Locations */}
              <div className="space-y-3">
                <Label className="font-medium">{t('workAbilityCard.workLocationPreferences')}</Label>
                <div className="flex flex-wrap gap-2">
                  {WORK_LOCATIONS.map((loc) => (
                    <Badge
                      key={loc.value}
                      variant={workLocations.includes(loc.value) ? 'default' : 'outline'}
                      className="cursor-pointer px-3 py-1"
                      onClick={() => toggleWorkLocation(loc.value)}
                    >
                      {workLocations.includes(loc.value) && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {t(loc.labelKey)}
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6">
              {/* Experience */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('workAbilityCard.totalYearsExperience')}</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={totalYears}
                    onChange={(e) => setTotalYears(parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('workAbilityCard.primaryIndustries')}</Label>
                  <Input
                    placeholder={t('workAbilityCard.primaryIndustriesPlaceholder')}
                    value={industries.join(', ')}
                    onChange={(e) => setIndustries(e.target.value.split(',').map(i => i.trim()).filter(Boolean))}
                  />
                </div>
              </div>

              {/* Headline */}
              <div className="space-y-2">
                <Label>{t('workAbilityCard.professionalHeadline')}</Label>
                <Input
                  placeholder={t('workAbilityCard.professionalHeadlinePlaceholder')}
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  maxLength={200}
                />
                <p className="text-xs text-gray-500">{headline.length}/200 {t('common.characters')}</p>
              </div>

              {/* Summary */}
              <div className="space-y-2">
                <Label>{t('workAbilityCard.professionalSummary')}</Label>
                <Textarea
                  placeholder={t('workAbilityCard.professionalSummaryPlaceholder')}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  maxLength={1000}
                  rows={4}
                />
                <p className="text-xs text-gray-500">{summary.length}/1000 {t('common.characters')}</p>
              </div>

              {/* Certifications */}
              <div className="space-y-3">
                <Label className="font-medium flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  {t('workAbilityCard.certifications')}
                </Label>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input
                      placeholder={t('workAbilityCard.certificationName')}
                      value={newCert.name}
                      onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
                    />
                    <Input
                      placeholder={t('workAbilityCard.issuingOrganization')}
                      value={newCert.issuer}
                      onChange={(e) => setNewCert({ ...newCert, issuer: e.target.value })}
                    />
                    <Button onClick={addCertification}>
                      <Plus className="h-4 w-4 mr-1" />
                      {t('common.add')}
                    </Button>
                  </div>
                </div>
                {certifications.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {certifications.map((cert, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="px-3 py-1.5 flex items-center gap-2"
                      >
                        <Award className="h-3 w-3" />
                        <span>{cert.name}</span>
                        {cert.issuer && <span className="text-xs text-gray-500">({cert.issuer})</span>}
                        <button
                          onClick={() => removeCertification(index)}
                          className="hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          size="lg"
          onClick={handleSave}
          disabled={saving || skills.length === 0 || preferredCountries.length === 0}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {wac ? t('workAbilityCard.updateButton') : t('workAbilityCard.createButton')}
        </Button>
      </div>
    </div>
  );
}

export default WorkAbilityCard;
